import { useState } from 'react';
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { App, Button, Card, Image, Space, Tag, theme, Typography, Upload } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';

import { uploadAttachmentByPresign } from '@/services/attachments';
import { resolveMiniDramaCdnUrl } from '@/utils/mini-drama-assets';
import {
    hasCustomThumbImage,
    moveBannerItem,
    removeBannerItem,
    resolveBannerTitle,
    resolveCustomThumbUrl,
    resolveDramaCoverUrl,
    updateBannerItem,
} from '@/pages/client-banner/utils';

import type { BannerEditorItem } from '@/types/theater-banner';

type Props = {
    items: BannerEditorItem[];
    disabled?: boolean;
    onChange: (items: BannerEditorItem[]) => void;
};

const PREVIEW_WIDTH = 120;
const PREVIEW_HEIGHT = 68;

const BannerList: React.FC<Props> = ({ items, disabled, onChange }) => {
    const { message } = App.useApp();
    const { formatMessage } = useIntl();
    const { token } = theme.useToken();
    const [uploadingId, setUploadingId] = useState<string | null>(null);

    const handleUpload = async (file: File, dramaId: string) => {
        setUploadingId(dramaId);
        try {
            const { path } = await uploadAttachmentByPresign(file);
            onChange(updateBannerItem(items, dramaId, { thumbUrl: resolveMiniDramaCdnUrl(path) }));
            message.success(formatMessage({ id: 'clientBanner.upload.success' }));
        } catch (error: any) {
            message.error(error?.message || formatMessage({ id: 'clientBanner.upload.failed' }));
        } finally {
            setUploadingId(null);
        }
    };

    if (!items.length) {
        return (
            <Typography.Text type="secondary">
                <FormattedMessage id="clientBanner.list.empty" />
            </Typography.Text>
        );
    }

    return (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {items.map((item, index) => {
                const customThumb = resolveCustomThumbUrl(item);
                const coverUrl = resolveDramaCoverUrl(item.drama);
                const previewUrl = customThumb || coverUrl;

                return (
                    <Card key={item.dramaId} size="small" styles={{ body: { padding: 12 } }}>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', minWidth: 0 }}>
                            <div
                                style={{
                                    width: PREVIEW_WIDTH,
                                    height: PREVIEW_HEIGHT,
                                    flex: 'none',
                                    borderRadius: token.borderRadius,
                                    overflow: 'hidden',
                                    background: token.colorFillAlter,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {previewUrl ? (
                                    <Image
                                        src={previewUrl}
                                        width={PREVIEW_WIDTH}
                                        height={PREVIEW_HEIGHT}
                                        style={{ objectFit: 'cover' }}
                                    />
                                ) : (
                                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                        <FormattedMessage id="clientBanner.list.noImage" />
                                    </Typography.Text>
                                )}
                            </div>

                            <Space
                                direction="vertical"
                                size={8}
                                style={{ flex: '1 1 0', width: 0, minWidth: 0, overflow: 'hidden' }}
                                styles={{ item: { minWidth: 0, maxWidth: '100%' } }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 4,
                                        minWidth: 0,
                                        maxWidth: '100%',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Tag style={{ flex: 'none', marginInlineEnd: 0 }}>{index + 1}</Tag>
                                    <Typography.Text
                                        strong
                                        ellipsis={{ tooltip: resolveBannerTitle(item) }}
                                        style={{ flex: '1 1 0', width: 0, minWidth: 0 }}
                                    >
                                        {resolveBannerTitle(item)}
                                    </Typography.Text>
                                    {item.missing && (
                                        <Tag color="error" style={{ flex: 'none', marginInlineEnd: 0 }}>
                                            <FormattedMessage id="clientBanner.list.missing" />
                                        </Tag>
                                    )}
                                    {hasCustomThumbImage(item) ? (
                                        <Tag color="blue" style={{ flex: 'none', marginInlineEnd: 0 }}>
                                            <FormattedMessage id="clientBanner.list.customThumb" />
                                        </Tag>
                                    ) : (
                                        <Tag style={{ flex: 'none', marginInlineEnd: 0 }}>
                                            <FormattedMessage id="clientBanner.list.usingDramaCover" />
                                        </Tag>
                                    )}
                                </div>
                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                    <FormattedMessage id="clientBanner.list.thumbHint" />
                                </Typography.Text>
                                <Space wrap>
                                    <Upload
                                        showUploadList={false}
                                        accept="image/*"
                                        disabled={disabled}
                                        customRequest={({ file, onSuccess, onError }) => {
                                            handleUpload(file as File, item.dramaId)
                                                .then(() => onSuccess?.('ok'))
                                                .catch(onError);
                                        }}
                                    >
                                        <Button
                                            size="small"
                                            icon={<UploadOutlined />}
                                            loading={uploadingId === item.dramaId}
                                            disabled={disabled}
                                        >
                                            <FormattedMessage id="clientBanner.list.uploadThumb" />
                                        </Button>
                                    </Upload>
                                    {hasCustomThumbImage(item) ? (
                                        <Button
                                            type="link"
                                            size="small"
                                            disabled={disabled}
                                            onClick={() =>
                                                onChange(updateBannerItem(items, item.dramaId, { thumbUrl: null }))
                                            }
                                        >
                                            <FormattedMessage id="clientBanner.list.restoreDefault" />
                                        </Button>
                                    ) : null}
                                </Space>
                            </Space>

                            <Space style={{ flex: 'none' }}>
                                <Button
                                    size="small"
                                    icon={<ArrowUpOutlined />}
                                    disabled={disabled || index === 0}
                                    onClick={() => onChange(moveBannerItem(items, index, 'up'))}
                                />
                                <Button
                                    size="small"
                                    icon={<ArrowDownOutlined />}
                                    disabled={disabled || index === items.length - 1}
                                    onClick={() => onChange(moveBannerItem(items, index, 'down'))}
                                />
                                <Button
                                    size="small"
                                    danger
                                    icon={<DeleteOutlined />}
                                    disabled={disabled}
                                    onClick={() => onChange(removeBannerItem(items, item.dramaId))}
                                />
                            </Space>
                        </div>
                    </Card>
                );
            })}
        </Space>
    );
};

export default BannerList;
